import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  Tooltip,
  Avatar,
  AvatarGroup,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Autocomplete,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Note as NoteIcon,
  PushPin as PinIcon,
  PushPinOutlined as PinOutlinedIcon,
  ViewList as ListViewIcon,
  GridView as GridViewIcon,
  AttachFile as AttachFileIcon,
  Archive as ArchiveIcon,
  MoreVert as MoreVertIcon,
  CheckBox as CheckboxIcon,
  CheckBoxOutlineBlank as CheckboxBlankIcon,
  ContentCopy as CopyIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Lock as LockIcon,
  Restore as RestoreIcon,
  DeleteForever as DeleteForeverIcon,
  ColorLens as ColorLensIcon,
  NotificationsActive as ReminderIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import api, { handleApiError } from '../../utils/api';

interface ChecklistItem {
  id: string;
  text: string;
  checked: boolean;
}

interface Attachment {
  id: string;
  name: string;
  type: string;
  size: number;
  url: string;
  thumbnailUrl?: string;
}

interface Reminder {
  id: string;
  date: string;
  time: string;
  notified: boolean;
}

interface Collaborator {
  email: string;
  name: string;
  permission: 'view' | 'edit';
  avatar?: string;
}

interface Version {
  id: string;
  content: string;
  title: string;
  timestamp: Date;
  author: string;
}

interface Note {
  id: string;
  title: string;
  content: string;
  color: string;
  pinned: boolean;
  starred: boolean;
  folderId: string;
  tags: string[];
  checklist: ChecklistItem[];
  isChecklist: boolean;
  attachments: Attachment[];
  reminder?: Reminder;
  collaborators: Collaborator[];
  isLocked: boolean;
  password?: string;
  archived: boolean;
  trashed: boolean;
  trashedAt?: Date;
  versions: Version[];
  createdAt: Date;
  updatedAt: Date;
  wordCount: number;
  characterCount: number;
}

interface Folder {
  id: string;
  name: string;
  color: string;
  icon: string;
  noteCount: number;
  isDefault: boolean;
}

type ViewMode = 'grid' | 'list';
type SortOption = 'updated' | 'created' | 'alphabetical' | 'color';

const noteColors = [
  { name: 'Default', value: '#ffffff' },
  { name: 'Red', value: '#f28b82' },
  { name: 'Orange', value: '#fbbc04' },
  { name: 'Yellow', value: '#fff475' },
  { name: 'Green', value: '#ccff90' },
  { name: 'Teal', value: '#a7ffeb' },
  { name: 'Blue', value: '#cbf0f8' },
  { name: 'Purple', value: '#d7aefb' },
  { name: 'Pink', value: '#fdcfe8' },
  { name: 'Brown', value: '#e6c9a8' },
  { name: 'Gray', value: '#e8eaed' },
];

const NotesPage: React.FC = () => {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);

  const mapNote = (n: any): Note => ({
    id: n._id || n.id,
    title: n.title || '',
    content: n.content || '',
    color: n.color || '#ffffff',
    pinned: n.pinned || false,
    starred: n.starred || false,
    folderId: n.folderId || 'all',
    tags: n.tags || [],
    checklist: (n.checklist || []).map((c: any) => ({ id: c.id || c._id || `c${Math.random()}`, text: c.text, checked: c.checked })),
    isChecklist: n.isChecklist || false,
    attachments: n.attachments || [],
    reminder: n.reminder,
    collaborators: n.collaborators || [],
    isLocked: n.isLocked || false,
    password: n.password,
    archived: n.archived || false,
    trashed: !!n.trashedAt || n.trashed || false,
    trashedAt: n.trashedAt ? new Date(n.trashedAt) : undefined,
    versions: n.versions || [],
    createdAt: new Date(n.createdAt),
    updatedAt: new Date(n.updatedAt),
    wordCount: n.wordCount || 0,
    characterCount: n.characterCount || 0,
  });

  const mapFolder = (f: any): Folder => ({
    id: f._id || f.id,
    name: f.name,
    color: f.color || '#1976d2',
    icon: f.icon || '📁',
    noteCount: f.noteCount || 0,
    isDefault: f.isDefault || false,
  });

  const fetchFolders = useCallback(async () => {
    try {
      const res = await api.get('/notes/folders');
      const data = res.data?.data || res.data || [];
      const apiFolders = Array.isArray(data) ? data.map(mapFolder) : [];
      if (!apiFolders.find(f => f.id === 'all' || f.name === 'All Notes')) {
        apiFolders.unshift({ id: 'all', name: 'All Notes', color: '#1976d2', icon: '📝', noteCount: 0, isDefault: true });
      }
      setFolders(apiFolders);
    } catch { /* ignore */ }
  }, []);

  const fetchNotes = useCallback(async () => {
    try {
      const res = await api.get('/notes');
      const data = res.data?.data || res.data || [];
      setNotes(Array.isArray(data) ? data.map(mapNote) : []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchFolders();
    fetchNotes();
  }, [fetchFolders, fetchNotes]);

  // Listen for global refresh event from header
  useEffect(() => {
    const handleRefresh = () => { fetchFolders(); fetchNotes(); };
    window.addEventListener('app-refresh', handleRefresh);
    return () => window.removeEventListener('app-refresh', handleRefresh);
  }, [fetchFolders, fetchNotes]);

  const [allTags] = useState<string[]>(['meeting', 'planning', 'important', 'development', 'features', 'shopping', 'private', 'journal', 'documentation', 'technical', 'personal', 'work']);

  const [searchQuery, setSearchQuery] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedFolderId, setSelectedFolderId] = useState<string>('all');
  const [sortOption, setSortOption] = useState<SortOption>('updated');
  const [tabValue, setTabValue] = useState(0); // 0: Notes, 1: Starred, 2: Archive, 3: Trash
  const [noteMenuAnchor, setNoteMenuAnchor] = useState<{ anchor: HTMLElement; note: Note } | null>(null);
  const [colorMenuAnchor, setColorMenuAnchor] = useState<{ anchor: HTMLElement; note: Note } | null>(null);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [unlockingNoteId, setUnlockingNoteId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'success' });
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderColor, setNewFolderColor] = useState('#1976d2');

  const [newNote, setNewNote] = useState<Partial<Note>>({
    title: '',
    content: '',
    color: '#ffffff',
    pinned: false,
    starred: false,
    folderId: 'personal',
    tags: [],
    checklist: [],
    isChecklist: false,
    attachments: [],
    collaborators: [],
    isLocked: false,
  });

  const [newCollaboratorEmail, setNewCollaboratorEmail] = useState('');
  const [newReminderDate, setNewReminderDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [newReminderTime, setNewReminderTime] = useState('09:00');

  const filteredNotes = useMemo(() => {
    let result = notes.filter(note => {
      // Tab filter
      if (tabValue === 0 && (note.archived || note.trashed)) return false;
      if (tabValue === 1 && (!note.starred || note.trashed)) return false;
      if (tabValue === 2 && !note.archived) return false;
      if (tabValue === 3 && !note.trashed) return false;

      // Folder filter
      if (selectedFolderId !== 'all' && note.folderId !== selectedFolderId && tabValue === 0) return false;

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return note.title.toLowerCase().includes(query) ||
               note.content.toLowerCase().includes(query) ||
               note.tags.some(t => t.toLowerCase().includes(query));
      }

      return true;
    });

    // Sort
    result.sort((a, b) => {
      // Pinned notes first
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;

      switch (sortOption) {
        case 'updated':
          return b.updatedAt.getTime() - a.updatedAt.getTime();
        case 'created':
          return b.createdAt.getTime() - a.createdAt.getTime();
        case 'alphabetical':
          return a.title.localeCompare(b.title);
        case 'color':
          return a.color.localeCompare(b.color);
        default:
          return 0;
      }
    });

    return result;
  }, [notes, selectedFolderId, tabValue, searchQuery, sortOption]);

  const pinnedNotes = filteredNotes.filter(n => n.pinned && !n.archived && !n.trashed);
  const otherNotes = filteredNotes.filter(n => !n.pinned || n.archived || n.trashed);

  const handleAddNote = () => {
    setEditingNote(null);
    setNewNote({
      title: '',
      content: '',
      color: '#ffffff',
      pinned: false,
      starred: false,
      folderId: selectedFolderId === 'all' ? 'personal' : selectedFolderId,
      tags: [],
      checklist: [],
      isChecklist: false,
      attachments: [],
      collaborators: [],
      isLocked: false,
    });
    setOpenDialog(true);
  };

  const handleEditNote = (note: Note) => {
    if (note.isLocked) {
      setUnlockingNoteId(note.id);
      setPasswordInput('');
      setPasswordDialogOpen(true);
      return;
    }
    openNoteForEdit(note);
  };

  const openNoteForEdit = (note: Note) => {
    setEditingNote(note);
    setNewNote({ ...note });
    setOpenDialog(true);
  };

  const handleUnlockNote = () => {
    const note = notes.find(n => n.id === unlockingNoteId);
    if (note && passwordInput === note.password) {
      setPasswordDialogOpen(false);
      openNoteForEdit(note);
    } else {
      setSnackbar({ open: true, message: 'Incorrect password', severity: 'error' });
    }
  };

  const handleSaveNote = async () => {
    if (!newNote.title?.trim() && !newNote.content?.trim() && (!newNote.checklist || newNote.checklist.length === 0)) {
      setSnackbar({ open: true, message: 'Note cannot be empty', severity: 'error' });
      return;
    }

    const dto: any = {
      title: newNote.title || 'Untitled',
      content: newNote.content || '',
      color: newNote.color || '#ffffff',
      pinned: newNote.pinned || false,
      starred: newNote.starred || false,
      folderId: newNote.folderId || 'personal',
      tags: newNote.tags || [],
      checklist: newNote.checklist || [],
      isChecklist: newNote.isChecklist || false,
      isLocked: newNote.isLocked || false,
      password: newNote.password,
      collaborators: newNote.collaborators || [],
      reminder: newNote.reminder,
    };

    try {
      if (editingNote) {
        const res = await api.put(`/notes/${editingNote.id}`, dto);
        const updated = mapNote(res.data?.data || res.data);
        setNotes(notes.map(n => n.id === editingNote.id ? updated : n));
        setSnackbar({ open: true, message: 'Note updated', severity: 'success' });
      } else {
        const res = await api.post('/notes', dto);
        const created = mapNote(res.data?.data || res.data);
        setNotes([created, ...notes]);
        setSnackbar({ open: true, message: 'Note created', severity: 'success' });
      }
      setOpenDialog(false);
    } catch (err) {
      setSnackbar({ open: true, message: handleApiError(err), severity: 'error' });
    }
  };

  const handleDeleteNote = async (id: string) => {
    const note = notes.find(n => n.id === id);
    if (!note) return;

    try {
      if (note.trashed) {
        await api.delete(`/notes/${id}`);
        setNotes(notes.filter(n => n.id !== id));
        setSnackbar({ open: true, message: 'Note permanently deleted', severity: 'success' });
      } else {
        await api.put(`/notes/${id}/trash`);
        setNotes(notes.map(n =>
          n.id === id ? { ...n, trashed: true, trashedAt: new Date() } : n
        ));
        setSnackbar({ open: true, message: 'Note moved to trash', severity: 'success' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: handleApiError(err), severity: 'error' });
    }
    setNoteMenuAnchor(null);
  };

  const handleRestoreNote = async (id: string) => {
    try {
      await api.put(`/notes/${id}/restore`);
      setNotes(notes.map(n =>
        n.id === id ? { ...n, trashed: false, trashedAt: undefined, archived: false } : n
      ));
      setSnackbar({ open: true, message: 'Note restored', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: handleApiError(err), severity: 'error' });
    }
    setNoteMenuAnchor(null);
  };

  const handleArchiveNote = async (id: string) => {
    const note = notes.find(n => n.id === id);
    try {
      if (note?.archived) {
        await api.put(`/notes/${id}/unarchive`);
      } else {
        await api.put(`/notes/${id}/archive`);
      }
      setNotes(notes.map(n =>
        n.id === id ? { ...n, archived: !n.archived } : n
      ));
      setSnackbar({ open: true, message: note?.archived ? 'Note unarchived' : 'Note archived', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: handleApiError(err), severity: 'error' });
    }
    setNoteMenuAnchor(null);
  };

  const handleTogglePin = async (id: string) => {
    try {
      await api.put(`/notes/${id}/pin`);
      setNotes(notes.map(n =>
        n.id === id ? { ...n, pinned: !n.pinned } : n
      ));
    } catch (err) {
      setSnackbar({ open: true, message: handleApiError(err), severity: 'error' });
    }
  };

  const handleToggleStar = async (id: string) => {
    try {
      await api.put(`/notes/${id}/star`);
      setNotes(notes.map(n =>
        n.id === id ? { ...n, starred: !n.starred } : n
      ));
    } catch (err) {
      setSnackbar({ open: true, message: handleApiError(err), severity: 'error' });
    }
  };

  const handleChangeColor = async (id: string, color: string) => {
    try {
      await api.put(`/notes/${id}`, { color });
      setNotes(notes.map(n =>
        n.id === id ? { ...n, color } : n
      ));
    } catch (err) {
      setSnackbar({ open: true, message: handleApiError(err), severity: 'error' });
    }
    setColorMenuAnchor(null);
  };

  const handleDuplicateNote = async (note: Note) => {
    try {
      const res = await api.post(`/notes/${note.id}/duplicate`);
      const created = mapNote(res.data?.data || res.data);
      setNotes([created, ...notes]);
      setSnackbar({ open: true, message: 'Note duplicated', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: handleApiError(err), severity: 'error' });
    }
    setNoteMenuAnchor(null);
  };

  const handleToggleChecklist = async (noteId: string, itemId: string) => {
    const note = notes.find(n => n.id === noteId);
    if (!note) return;
    const updatedChecklist = note.checklist.map(item =>
      item.id === itemId ? { ...item, checked: !item.checked } : item
    );
    try {
      await api.put(`/notes/${noteId}`, { checklist: updatedChecklist });
      setNotes(notes.map(n => {
        if (n.id !== noteId) return n;
        return { ...n, checklist: updatedChecklist, updatedAt: new Date() };
      }));
    } catch { /* ignore */ }
  };

  const handleAddChecklistItem = () => {
    const newItem: ChecklistItem = {
      id: `c${Date.now()}`,
      text: '',
      checked: false,
    };
    setNewNote({
      ...newNote,
      checklist: [...(newNote.checklist || []), newItem],
    });
  };

  const handleAddCollaborator = () => {
    if (!newCollaboratorEmail.trim()) return;
    const newCollab: Collaborator = {
      email: newCollaboratorEmail,
      name: newCollaboratorEmail.split('@')[0],
      permission: 'view',
    };
    setNewNote({
      ...newNote,
      collaborators: [...(newNote.collaborators || []), newCollab],
    });
    setNewCollaboratorEmail('');
  };

  const handleSetReminder = () => {
    setNewNote({
      ...newNote,
      reminder: {
        id: `r${Date.now()}`,
        date: newReminderDate,
        time: newReminderTime,
        notified: false,
      },
    });
    setReminderDialogOpen(false);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const res = await api.post('/notes/folders', { name: newFolderName, color: newFolderColor, icon: '📁' });
      const created = mapFolder(res.data?.data || res.data);
      setFolders(prev => [...prev, created]);
      setNewFolderName('');
      setFolderDialogOpen(false);
      setSnackbar({ open: true, message: 'Folder created', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: handleApiError(err), severity: 'error' });
    }
  };

  const getChecklistProgress = (checklist: ChecklistItem[]) => {
    if (checklist.length === 0) return 0;
    return Math.round((checklist.filter(c => c.checked).length / checklist.length) * 100);
  };

  const NoteCard = ({ note }: { note: Note }) => {
    const checklistProgress = getChecklistProgress(note.checklist);

    return (
      <Card
        sx={{
          bgcolor: note.color,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          '&:hover': { boxShadow: 4 },
          cursor: 'pointer',
          position: 'relative',
          opacity: note.isLocked ? 0.9 : 1,
        }}
        onClick={() => handleEditNote(note)}
      >
        {note.isLocked && (
          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
            <LockIcon fontSize="small" color="action" />
          </Box>
        )}

        <CardContent sx={{ flex: 1, pb: 1 }}>
          {note.title && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {note.title}
              </Typography>
              {note.starred && <StarIcon fontSize="small" color="warning" />}
            </Box>
          )}

          {note.isChecklist ? (
            <Box>
              {note.checklist.slice(0, 5).map(item => (
                <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25 }}>
                  <Checkbox
                    size="small"
                    checked={item.checked}
                    onClick={(e) => { e.stopPropagation(); handleToggleChecklist(note.id, item.id); }}
                    sx={{ p: 0 }}
                    icon={<CheckboxBlankIcon fontSize="small" />}
                    checkedIcon={<CheckboxIcon fontSize="small" />}
                  />
                  <Typography
                    variant="body2"
                    sx={{ 
                      textDecoration: item.checked ? 'line-through' : 'none',
                      color: item.checked ? 'text.secondary' : 'inherit',
                      fontSize: '0.8rem',
                    }}
                  >
                    {item.text}
                  </Typography>
                </Box>
              ))}
              {note.checklist.length > 5 && (
                <Typography variant="caption" color="text.secondary">
                  +{note.checklist.length - 5} more items
                </Typography>
              )}
              {note.checklist.length > 0 && (
                <LinearProgress
                  variant="determinate"
                  value={checklistProgress}
                  sx={{ mt: 1, height: 4, borderRadius: 2 }}
                />
              )}
            </Box>
          ) : (
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 6,
                WebkitBoxOrient: 'vertical',
                fontSize: '0.85rem',
              }}
            >
              {note.isLocked ? '••••••••••••••••' : note.content}
            </Typography>
          )}

          {/* Tags */}
          {note.tags.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
              {note.tags.slice(0, 3).map(tag => (
                <Chip key={tag} label={tag} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
              ))}
              {note.tags.length > 3 && (
                <Chip label={`+${note.tags.length - 3}`} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
              )}
            </Box>
          )}

          {/* Attachments indicator */}
          {note.attachments.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
              <AttachFileIcon sx={{ fontSize: 14 }} color="action" />
              <Typography variant="caption" color="text.secondary">
                {note.attachments.length} attachment{note.attachments.length > 1 ? 's' : ''}
              </Typography>
            </Box>
          )}

          {/* Collaborators */}
          {note.collaborators.length > 0 && (
            <AvatarGroup max={3} sx={{ mt: 1, justifyContent: 'flex-start' }}>
              {note.collaborators.map((collab, idx) => (
                <Tooltip key={idx} title={collab.name}>
                  <Avatar sx={{ width: 20, height: 20, fontSize: '0.6rem' }}>
                    {collab.name.charAt(0).toUpperCase()}
                  </Avatar>
                </Tooltip>
              ))}
            </AvatarGroup>
          )}

          {/* Reminder indicator */}
          {note.reminder && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
              <ReminderIcon sx={{ fontSize: 14 }} color="primary" />
              <Typography variant="caption" color="primary">
                {format(parseISO(`${note.reminder.date}T${note.reminder.time}`), 'MMM d, h:mm a')}
              </Typography>
            </Box>
          )}
        </CardContent>

        <CardActions sx={{ justifyContent: 'space-between', pt: 0, px: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {formatDistanceToNow(note.updatedAt, { addSuffix: true })}
          </Typography>
          <Box>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); handleTogglePin(note.id); }}
            >
              {note.pinned ? <PinIcon fontSize="small" /> : <PinOutlinedIcon fontSize="small" />}
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setNoteMenuAnchor({ anchor: e.currentTarget, note }); }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>
        </CardActions>
      </Card>
    );
  };

  const totalNotes = notes.filter(n => !n.trashed).length;
  const starredCount = notes.filter(n => n.starred && !n.trashed).length;
  const archivedCount = notes.filter(n => n.archived).length;
  const trashedCount = notes.filter(n => n.trashed).length;

  return (
    <Box sx={{ height: '100%', display: 'flex', bgcolor: '#f5f5f5' }}>
      {/* Left Sidebar */}
      <Paper sx={{ width: 240, m: 2, mr: 0, p: 2, display: 'flex', flexDirection: 'column' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddNote}
          sx={{ mb: 2 }}
          fullWidth
        >
          New Note
        </Button>

        {/* Tabs */}
        <List dense disablePadding sx={{ mb: 2 }}>
          <ListItemButton
            selected={tabValue === 0}
            onClick={() => setTabValue(0)}
            sx={{ borderRadius: 1, mb: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}><NoteIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Notes" />
            <Typography variant="caption" color="text.secondary">{totalNotes}</Typography>
          </ListItemButton>
          <ListItemButton
            selected={tabValue === 1}
            onClick={() => setTabValue(1)}
            sx={{ borderRadius: 1, mb: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}><StarIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Starred" />
            <Typography variant="caption" color="text.secondary">{starredCount}</Typography>
          </ListItemButton>
          <ListItemButton
            selected={tabValue === 2}
            onClick={() => setTabValue(2)}
            sx={{ borderRadius: 1, mb: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}><ArchiveIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Archive" />
            <Typography variant="caption" color="text.secondary">{archivedCount}</Typography>
          </ListItemButton>
          <ListItemButton
            selected={tabValue === 3}
            onClick={() => setTabValue(3)}
            sx={{ borderRadius: 1, mb: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}><DeleteIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Trash" />
            <Typography variant="caption" color="text.secondary">{trashedCount}</Typography>
          </ListItemButton>
        </List>

        <Divider sx={{ my: 1 }} />

        {/* Folders */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>FOLDERS</Typography>
          <IconButton size="small" onClick={() => setFolderDialogOpen(true)}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>

        <List dense disablePadding>
          {folders.map(folder => (
            <ListItemButton
              key={folder.id}
              selected={selectedFolderId === folder.id && tabValue === 0}
              onClick={() => { setSelectedFolderId(folder.id); setTabValue(0); }}
              sx={{ borderRadius: 1, mb: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Typography>{folder.icon}</Typography>
              </ListItemIcon>
              <ListItemText primary={folder.name} />
              <Typography variant="caption" color="text.secondary">
                {notes.filter(n => (folder.id === 'all' || n.folderId === folder.id) && !n.trashed && !n.archived).length}
              </Typography>
            </ListItemButton>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        {/* Tags */}
        <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>TAGS</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {allTags.slice(0, 8).map(tag => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
              onClick={() => setSearchQuery(tag)}
            />
          ))}
        </Box>
      </Paper>

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
        {/* Header */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5" fontWeight={600} sx={{ mr: 'auto' }}>
              {tabValue === 0 ? (selectedFolderId === 'all' ? 'All Notes' : folders.find(f => f.id === selectedFolderId)?.name) :
               tabValue === 1 ? 'Starred' :
               tabValue === 2 ? 'Archive' : 'Trash'}
            </Typography>
            <TextField
              size="small"
              placeholder="Search notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
              }}
              sx={{ width: 300 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value as SortOption)}
              >
                <MenuItem value="updated">Last updated</MenuItem>
                <MenuItem value="created">Date created</MenuItem>
                <MenuItem value="alphabetical">Alphabetical</MenuItem>
                <MenuItem value="color">Color</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title={viewMode === 'grid' ? 'List view' : 'Grid view'}>
              <IconButton onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}>
                {viewMode === 'grid' ? <ListViewIcon /> : <GridViewIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>

        {/* Notes Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {filteredNotes.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <NoteIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                {searchQuery ? 'No notes found' : 
                 tabValue === 2 ? 'No archived notes' :
                 tabValue === 3 ? 'Trash is empty' : 'No notes yet'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {searchQuery ? 'Try a different search term' : 
                 tabValue === 3 ? 'Deleted notes will appear here' :
                 'Click "New Note" to create your first note'}
              </Typography>
              {!searchQuery && tabValue === 0 && (
                <Button startIcon={<AddIcon />} variant="contained" onClick={handleAddNote}>
                  Create Note
                </Button>
              )}
            </Box>
          ) : (
            <>
              {/* Pinned Notes */}
              {pinnedNotes.length > 0 && tabValue === 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ px: 1 }}>
                    Pinned ({pinnedNotes.length})
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    {pinnedNotes.map(note => (
                      <Grid key={note.id} size={{ xs: 12, sm: 6, md: viewMode === 'grid' ? 4 : 12, lg: viewMode === 'grid' ? 3 : 12 }}>
                        <NoteCard note={note} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Other Notes */}
              {otherNotes.length > 0 && (
                <Box>
                  {pinnedNotes.length > 0 && tabValue === 0 && (
                    <Typography variant="overline" color="text.secondary" sx={{ px: 1 }}>
                      Others ({otherNotes.length})
                    </Typography>
                  )}
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    {otherNotes.map(note => (
                      <Grid key={note.id} size={{ xs: 12, sm: 6, md: viewMode === 'grid' ? 4 : 12, lg: viewMode === 'grid' ? 3 : 12 }}>
                        <NoteCard note={note} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Note Context Menu */}
      <Menu
        anchorEl={noteMenuAnchor?.anchor}
        open={Boolean(noteMenuAnchor)}
        onClose={() => setNoteMenuAnchor(null)}
      >
        {noteMenuAnchor?.note.trashed ? (
          <>
            <MenuItem onClick={() => noteMenuAnchor && handleRestoreNote(noteMenuAnchor.note.id)}>
              <ListItemIcon><RestoreIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Restore</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => noteMenuAnchor && handleDeleteNote(noteMenuAnchor.note.id)}>
              <ListItemIcon><DeleteForeverIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>Delete Forever</ListItemText>
            </MenuItem>
          </>
        ) : (
          <>
            <MenuItem onClick={() => noteMenuAnchor && handleToggleStar(noteMenuAnchor.note.id)}>
              <ListItemIcon>{noteMenuAnchor?.note.starred ? <StarIcon fontSize="small" color="warning" /> : <StarBorderIcon fontSize="small" />}</ListItemIcon>
              <ListItemText>{noteMenuAnchor?.note.starred ? 'Unstar' : 'Star'}</ListItemText>
            </MenuItem>
            <MenuItem onClick={(e) => noteMenuAnchor && setColorMenuAnchor({ anchor: e.currentTarget, note: noteMenuAnchor.note })}>
              <ListItemIcon><ColorLensIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Change color</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => noteMenuAnchor && handleDuplicateNote(noteMenuAnchor.note)}>
              <ListItemIcon><CopyIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Duplicate</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => noteMenuAnchor && handleArchiveNote(noteMenuAnchor.note.id)}>
              <ListItemIcon><ArchiveIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{noteMenuAnchor?.note.archived ? 'Unarchive' : 'Archive'}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => noteMenuAnchor && handleDeleteNote(noteMenuAnchor.note.id)}>
              <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>Move to Trash</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Color Picker Menu */}
      <Menu
        anchorEl={colorMenuAnchor?.anchor}
        open={Boolean(colorMenuAnchor)}
        onClose={() => setColorMenuAnchor(null)}
      >
        <Box sx={{ display: 'flex', gap: 0.5, p: 1, flexWrap: 'wrap', maxWidth: 200 }}>
          {noteColors.map(color => (
            <Box
              key={color.value}
              onClick={() => colorMenuAnchor && handleChangeColor(colorMenuAnchor.note.id, color.value)}
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: color.value,
                border: colorMenuAnchor?.note.color === color.value ? '2px solid #1976d2' : '1px solid #ccc',
                cursor: 'pointer',
                '&:hover': { transform: 'scale(1.1)' },
              }}
            />
          ))}
        </Box>
      </Menu>

      {/* Add/Edit Note Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{editingNote ? 'Edit Note' : 'New Note'}</span>
            <Box>
              <Tooltip title={newNote.isChecklist ? 'Switch to text' : 'Switch to checklist'}>
                <IconButton onClick={() => setNewNote({ ...newNote, isChecklist: !newNote.isChecklist })}>
                  {newNote.isChecklist ? <NoteIcon /> : <CheckboxIcon />}
                </IconButton>
              </Tooltip>
              <Tooltip title={newNote.pinned ? 'Unpin' : 'Pin'}>
                <IconButton onClick={() => setNewNote({ ...newNote, pinned: !newNote.pinned })}>
                  {newNote.pinned ? <PinIcon /> : <PinOutlinedIcon />}
                </IconButton>
              </Tooltip>
              <Tooltip title={newNote.starred ? 'Unstar' : 'Star'}>
                <IconButton onClick={() => setNewNote({ ...newNote, starred: !newNote.starred })}>
                  {newNote.starred ? <StarIcon color="warning" /> : <StarBorderIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: newNote.color }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Title"
                fullWidth
                value={newNote.title}
                onChange={(e) => setNewNote({ ...newNote, title: e.target.value })}
                sx={{ bgcolor: 'white' }}
              />
            </Grid>

            {newNote.isChecklist ? (
              <Grid size={{ xs: 12 }}>
                <Box>
                  {newNote.checklist?.map((item, idx) => (
                    <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Checkbox
                        size="small"
                        checked={item.checked}
                        onChange={(e) => {
                          const checklist = [...(newNote.checklist || [])];
                          checklist[idx] = { ...checklist[idx], checked: e.target.checked };
                          setNewNote({ ...newNote, checklist });
                        }}
                      />
                      <TextField
                        size="small"
                        fullWidth
                        value={item.text}
                        placeholder="List item"
                        onChange={(e) => {
                          const checklist = [...(newNote.checklist || [])];
                          checklist[idx] = { ...checklist[idx], text: e.target.value };
                          setNewNote({ ...newNote, checklist });
                        }}
                        sx={{ bgcolor: 'white' }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          const checklist = newNote.checklist?.filter((_, i) => i !== idx) || [];
                          setNewNote({ ...newNote, checklist });
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  <Button size="small" startIcon={<AddIcon />} onClick={handleAddChecklistItem}>
                    Add Item
                  </Button>
                </Box>
              </Grid>
            ) : (
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Content"
                  fullWidth
                  multiline
                  rows={8}
                  value={newNote.content}
                  onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
            )}

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Folder</InputLabel>
                <Select
                  value={newNote.folderId}
                  label="Folder"
                  onChange={(e) => setNewNote({ ...newNote, folderId: e.target.value })}
                  sx={{ bgcolor: 'white' }}
                >
                  {folders.filter(f => f.id !== 'all').map(folder => (
                    <MenuItem key={folder.id} value={folder.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>{folder.icon}</Typography>
                        {folder.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                multiple
                freeSolo
                options={allTags}
                value={newNote.tags || []}
                onChange={(_, newValue) => setNewNote({ ...newNote, tags: newValue })}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} {...getTagProps({ index })} size="small" key={index} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Tags" placeholder="Add tags" sx={{ bgcolor: 'white' }} />
                )}
              />
            </Grid>

            {/* Color Picker */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Color</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {noteColors.map(color => (
                  <Box
                    key={color.value}
                    onClick={() => setNewNote({ ...newNote, color: color.value })}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: color.value,
                      border: newNote.color === color.value ? '2px solid #1976d2' : '1px solid #ccc',
                      cursor: 'pointer',
                      '&:hover': { transform: 'scale(1.1)' },
                    }}
                  />
                ))}
              </Box>
            </Grid>

            {/* Reminder */}
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">Reminder:</Typography>
                {newNote.reminder ? (
                  <Chip
                    icon={<ReminderIcon />}
                    label={`${newNote.reminder.date} ${newNote.reminder.time}`}
                    onDelete={() => setNewNote({ ...newNote, reminder: undefined })}
                    color="primary"
                    size="small"
                  />
                ) : (
                  <Button size="small" startIcon={<ReminderIcon />} onClick={() => setReminderDialogOpen(true)}>
                    Add Reminder
                  </Button>
                )}
              </Box>
            </Grid>

            {/* Lock */}
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newNote.isLocked || false}
                    onChange={(e) => setNewNote({ ...newNote, isLocked: e.target.checked })}
                  />
                }
                label="Password protect this note"
              />
              {newNote.isLocked && (
                <TextField
                  type="password"
                  size="small"
                  label="Password"
                  value={newNote.password || ''}
                  onChange={(e) => setNewNote({ ...newNote, password: e.target.value })}
                  sx={{ ml: 2, width: 200, bgcolor: 'white' }}
                />
              )}
            </Grid>

            {/* Collaborators */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Collaborators</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                {newNote.collaborators?.map((collab, idx) => (
                  <Chip
                    key={idx}
                    avatar={<Avatar sx={{ width: 24, height: 24 }}>{collab.name.charAt(0)}</Avatar>}
                    label={`${collab.name} (${collab.permission})`}
                    onDelete={() => {
                      const collaborators = newNote.collaborators?.filter((_, i) => i !== idx) || [];
                      setNewNote({ ...newNote, collaborators });
                    }}
                    size="small"
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  placeholder="Email address"
                  value={newCollaboratorEmail}
                  onChange={(e) => setNewCollaboratorEmail(e.target.value)}
                  sx={{ flex: 1, bgcolor: 'white' }}
                />
                <Button size="small" variant="outlined" onClick={handleAddCollaborator}>
                  Add
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveNote} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Reminder Dialog */}
      <Dialog open={reminderDialogOpen} onClose={() => setReminderDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Set Reminder</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                type="date"
                label="Date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newReminderDate}
                onChange={(e) => setNewReminderDate(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                type="time"
                label="Time"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newReminderTime}
                onChange={(e) => setNewReminderTime(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReminderDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSetReminder} variant="contained">Set Reminder</Button>
        </DialogActions>
      </Dialog>

      {/* Password Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LockIcon /> Unlock Note
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            type="password"
            label="Enter password"
            fullWidth
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleUnlockNote()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUnlockNote} variant="contained">Unlock</Button>
        </DialogActions>
      </Dialog>

      {/* Create Folder Dialog */}
      <Dialog open={folderDialogOpen} onClose={() => setFolderDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Folder Name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Color</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {['#1976d2', '#4caf50', '#ff9800', '#f44336', '#9c27b0', '#009688', '#e91e63'].map(color => (
                <Box
                  key={color}
                  onClick={() => setNewFolderColor(color)}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: color,
                    border: newFolderColor === color ? '2px solid #000' : '1px solid #ccc',
                    cursor: 'pointer',
                  }}
                />
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFolderDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateFolder} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotesPage;
